import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@tanstack/react-query';
import * as Yup from 'yup';
import DeliverablesComponent from './DeliverablesComponent';
import OverviewComponent from './OverviewComponent';
import { getSingleContract, setContractOffer } from '../../services/procurementService';

import ConfirmationModal from '../../components/ConfirmModal/ConfirmationModal';
import { ErrorMessage, Form, Formik } from 'formik';
import FileUpload from '../../components/FileUpload/file-upload';

import { useTranslation } from 'react-i18next';

const ContractsDetailsPage = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const isSupplier = localStorage.getItem('supplierId');

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'Overview';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  //   Fetch tender documents
  const { data: singleContract } = useQuery({
    queryKey: ['contractById', contractId, activeTab],
    queryFn: async () => {
      const response = await getSingleContract(contractId as string);

      return response.data;
    },
    enabled: !!contractId,
    // staleTime: 3 * 60 * 1000, // Cache data for 3 minutes
    refetchOnWindowFocus: false, // Do not refetch when the window regains focus
  });

  const setMilestoneMutation = useMutation({
    mutationFn: (payload: any) => setContractOffer(payload),
    onSuccess: (response) => {
      if (response.isSuccess) {
        toast.success(response.data.message);
      } else {
        toast.error(response.errorMessage);
      }
    },
    onError: (error) => {
      console.error(error);
      toast.error('Unable to perform action, please try again later');
    },
  });

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Sync the active tab with query params when the component mounts
  useEffect(() => {
    if (searchParams.get('tab') !== activeTab) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, searchParams, setSearchParams]);

  const handleConfirm = async () => {
    const formData = new FormData();

    formData.append('contractId', contractId as string);
    formData.append('status', 'Rejected');
    formData.append('comment', '');

    setMilestoneMutation.mutate(formData);

    setConfirmationModalOpen(false);
  };

  return (
    <div className="mx-auto max-w-screen-xl p-8">
      <div className="2xl:w-11/12">
        <div className="min-h-[90vh] rounded-2xl border border-[#E5E5E5] bg-white pb-[54px] pl-[34px] pr-[26px] pt-[33px]">
          <div className="mb-6 flex space-x-8 border-b border-gray-200">
            {['Overview', 'Deliverables'].map((tab) => (
              <button
                key={tab}
                className={`px-4 py-3 text-sm text-[#525252] ${activeTab === tab ? 'border-b-2 border-primary font-semibold' : ''}`}
                onClick={() => handleTabChange(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {isSupplier && singleContract?.status?.toLocaleLowerCase() === 'pending' ? (
            <div className="mb-12">
              <div className="rounded-[4px] border-2 border-[#42BE65] bg-[#DFF7E6] px-4 py-3">
                <p className="text-base">
                  <span className="font-semibold text-[#161616]">Congratulations!!</span> You have
                  been selected for this opportunity
                </p>
              </div>

              <div className="mt-8 flex justify-end gap-x-4">
                <button
                  className="h-[48px] w-[132px] rounded-lg bg-[#F5F5F5] px-5 py-3 font-semibold text-[#FA4D56] transition duration-200 hover:bg-[#d93f3f] hover:text-white"
                  onClick={() => setConfirmationModalOpen(true)}
                >
                  Reject
                </button>

                <button
                  className="h-[48px] w-[132px] rounded-lg bg-[#24A148] px-5 py-3 font-semibold text-white transition duration-200 hover:bg-[#1E8A3D]"
                  onClick={() => {
                    // Accept the contract
                    setIsAddModalOpen(true);
                  }}
                >
                  Accept
                </button>
              </div>
            </div>
          ) : null}

          <div className="grid items-center gap-y-6 xl:grid-cols-[1fr_1fr]">
            <div className="flex items-center gap-x-6">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/ShellPix.svg`}
                alt="vendor icon"
                className="h-[58px] w-[58px] rounded-full"
              />

              <div className="flex flex-col items-start gap-y-2">
                <span className="text-sm text-[#8D8D8D]">Contractor Name:</span>
                <span className="font-semibold text-[#101828] underline">
                  {singleContract?.clientName}
                </span>

                <span className="rounded-[30px] bg-[#42BE65] px-2.5 py-1 text-[10px] font-medium text-white">
                  Verified Operator
                </span>
              </div>
            </div>

            <div className="grid grid-cols-[1.5fr_1fr]">
              <div className="flex flex-col gap-y-2">
                <span className="text-sm text-[#525252]">Contract Value:</span>
                <span className="text-base font-medium text-[#161616]">
                  ${new Intl.NumberFormat('en-US').format(singleContract?.amount ?? 0)}
                </span>
              </div>

              <div className="flex flex-col gap-y-2">
                <span className="text-sm text-[#525252]">Contract ID:</span>
                <span className="text-base font-medium text-[#161616]">
                  {singleContract?.contractId}
                </span>
              </div>
            </div>
          </div>

          {activeTab === 'Overview' && singleContract && (
            <OverviewComponent singleContract={singleContract} />
          )}
          {activeTab === 'Deliverables' && <DeliverablesComponent />}
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        onConfirm={handleConfirm}
        title="Confirm Action"
        message={`Are you sure you want to reject this contract?`}
      />

      {isAddModalOpen && (
        <AddDocument
          contractId={contractId as string}
          onClose={() => {
            setIsAddModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

interface AddDocumentProps {
  contractId: string;
  onClose: () => void;
}

const validationSchema = Yup.object({
  documents: Yup.mixed()
    .required('Document is required')
    .test('fileRequired', 'Document is required', (value) => {
      // Check if the value is a File object and not an empty object
      return value instanceof File && value.size > 0;
    }),
});

const AddDocument: React.FC<AddDocumentProps> = ({ contractId, onClose }) => {
  const { t } = useTranslation();

  const setMilestoneMutation = useMutation({
    mutationFn: (payload: any) => setContractOffer(payload),
    onSuccess: (response) => {
      if (response.isSuccess) {
        toast.success(response.data.message);
      } else {
        toast.error(response.errorMessage);
      }
    },
    onError: (error) => {
      console.error(error);
      toast.error('Unable to perform action, please try again later');
    },
  });

  const handleSubmit = async (values: any) => {
    console.log('values: ', values);

    const formData = new FormData();

    formData.append('contractId', contractId);
    formData.append('status', 'Accepted');
    formData.append('comment', '');
    formData.append('file', values.documents);

    await setMilestoneMutation.mutate(formData);

    onClose();
  };

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-gray-600 bg-opacity-50">
      <div className="w-full max-w-[90%] rounded-2xl bg-white p-8 shadow-lg lg:max-w-3xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Sign Contract</h2>
          <button className="text-xl text-gray-500" onClick={onClose}>
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/close.svg`}
              alt={t('close', 'close')}
            />
          </button>
        </div>

        <Formik
          initialValues={{
            documents: {},
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values }) => (
            <Form>
              <div className="grid grid-cols-2 gap-6">
                {/* Upload Documents */}
                <div className="col-span-2 mb-4">
                  <label className="mb-1 block text-sm font-medium text-[#344054]">
                    Upload signed Contract-offer.pdf document{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <FileUpload
                    onFileSelect={(file: File | null) => setFieldValue('documents', file)}
                    showUpload={false}
                    maxSize={3000 * 1024}
                    showNote={false}
                  />
                  <ErrorMessage
                    name="documents"
                    component="span"
                    className="text-sm text-red-500"
                  />
                </div>
              </div>

              <div className="divider my-6 !bg-[#E5E5E5]"></div>

              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  //   onClick={() => {
                  //     console.log('values: ', values);
                  //   }}
                  className="h-12 cursor-pointer rounded-lg bg-primary px-12 py-2 font-semibold text-black"
                >
                  {t('save', 'Save')}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ContractsDetailsPage;
